import React from 'react';
import { APP_NAME } from '../../utils/brand';

/**
 * A Portero jel: három emelkedő pont a csapatot, a legnagyobb körbe vágott pipa
 * a részvételt jelöli. A kitöltés `currentColor`, tehát a szülő szövegszínét
 * veszi fel - így ugyanez az egy path szolgál ki világos és sötét hátteret.
 *
 * A jelet nem színezzük a klub színére: a klubszín a felületen jelenik meg, a
 * márkajelen nem (arculati útmutató, "Amit ne csinálj").
 */
export const BrandMark: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style }) => (
  <svg
    viewBox="136 114 274 270"
    className={className}
    style={style}
    role="img"
    aria-label={APP_NAME}
    focusable="false"
  >
    <path
      fillRule="evenodd"
      fill="currentColor"
      d="M136,344 A40,40 0 1,0 216,344 A40,40 0 1,0 136,344 Z M204,268 A52,52 0 1,0 308,268 A52,52 0 1,0 204,268 Z M278,180 A66,66 0 1,0 410,180 A66,66 0 1,0 278,180 Z M327.9,194.6 L308.7,175.4 L302.2,181.9 L327.9,207.6 L383.1,152.4 L376.6,145.9 Z"
    />
  </svg>
);

type LockupSize = 'sm' | 'md' | 'lg';

/**
 * A vízszintes lockup: jel + felirat. A feliratot nem képként húzzuk be, hanem
 * a lapon már betöltött Fraunces-szal szedjük (`.wordmark`, lásd index.css) -
 * egy különálló SVG-ben a betűtípus nem töltődne be, és Georgiára esne vissza.
 *
 * A jel a márka fix blue-600-ja, nem a `primary` skáláé: az utóbbit a klub
 * felülírhatja, és a jelnek épp attól kell függetlennek maradnia. A felirat a
 * szöveg színe; `inverted` mellett mindkettő fehér, sötét háttérhez.
 */
const BRAND_BLUE = '#2563eb';

export const BrandLockup: React.FC<{
  size?: LockupSize;
  inverted?: boolean;
  className?: string;
}> = ({ size = 'md', inverted = false, className = '' }) => {
  const dimensions: Record<LockupSize, { mark: string; text: string; gap: string }> = {
    sm: { mark: 'h-6 w-6', text: 'text-[22px]', gap: 'gap-2' },
    md: { mark: 'h-8 w-8', text: 'text-[28px]', gap: 'gap-2.5' },
    lg: { mark: 'h-14 w-14', text: 'text-[46px]', gap: 'gap-3.5' },
  };
  const { mark, text, gap } = dimensions[size];

  return (
    <span className={`inline-flex items-center whitespace-nowrap ${gap} ${className}`}>
      <BrandMark
        className={`${mark} flex-none ${inverted ? 'text-white' : ''}`}
        style={inverted ? undefined : { color: BRAND_BLUE }}
      />
      <span className={`wordmark ${text} ${inverted ? 'text-white' : 'text-gray-900'}`}>
        {APP_NAME}
      </span>
    </span>
  );
};

export default BrandLockup;
